import React, {useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router'
import {checkIsActive, KTSVG, WithChildren} from '../../../../helpers'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  msIcon?: string
  hasBullet?: boolean
}

const SidebarMenuItemWithSub: React.FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  msIcon,
  hasBullet,
}) => {
  const {pathname} = useLocation()
  const childItems = useMemo(() => React.Children.toArray(children), [children])
  const pathOnly = (to?: string) => (to || '').split('?')[0]
  const collectChildTos = (nodes: any[]): string[] => {
    const result: string[] = []
    nodes.forEach((child: any) => {
      if (child?.props?.to) result.push(pathOnly(child.props.to))
      if (child?.props?.children) {
        result.push(...collectChildTos(React.Children.toArray(child.props.children)))
      }
    })
    return result
  }
  const childPaths = collectChildTos(childItems)
  const isActive =
    childPaths.some((p) => p && pathname.startsWith(p)) ||
    (!!to && pathname.startsWith(pathOnly(to)))
  const [isOpen, setIsOpen] = useState(isActive)

  useEffect(() => {
    if (isActive) {
      setIsOpen(true)
    }
  }, [isActive])

  return (
    <div className='mb-0.5'>
      <button
        type='button'
        className={[
          'w-100 d-flex align-items-center gap-3 px-3 py-2.5 rounded-lg text-sm border-0 bg-transparent transition-all duration-150',
          isActive ? 'font-bold' : 'font-medium hover:bg-slate-200/50',
        ].join(' ')}
        style={{color: isActive || isOpen ? '#1d4ed8' : '#64748b'}}
        onClick={() => setIsOpen((v) => !v)}
      >
        {/* Material Symbol icon (preferred) */}
        {msIcon && (
          <span
            className='material-symbols-outlined flex-shrink-0'
            style={{
              fontSize: '20px',
              fontVariationSettings: isActive
                ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
            }}
          >
            {msIcon}
          </span>
        )}
        {/* Fallback: SVG icon */}
        {!msIcon && icon && (
          <span className='flex-shrink-0'>
            <KTSVG path={icon} className='svg-icon-2' />
          </span>
        )}
        {/* Fallback: bullet */}
        {!msIcon && !icon && hasBullet && (
          <span className='flex-shrink-0 w-1.5 h-1.5 rounded-full bg-current opacity-40 ml-1' />
        )}

        <span className='flex-1 text-start text-truncate'>{title}</span>
        <span
          className='material-symbols-outlined'
          style={{
            fontSize: '18px',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          chevron_right
        </span>
      </button>

      {isOpen && <div className='ms-2 ps-3 pt-1 border-start border-gray-300'>{children}</div>}
    </div>
  )
}

export {SidebarMenuItemWithSub}
